import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import LegalAcceptCheckbox from "@/components/LegalAcceptCheckbox";
import BookWithoutAccountCTA from "@/components/BookWithoutAccountCTA";

export default function RegisterAgencyPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", contact_name: "", email: "", phone: "", password: "", accepted_terms: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error("Agency name, email & password are required");
      return;
    }
    if (!form.accepted_terms) {
      toast.error("Vous devez accepter les conditions générales");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register-agency", { ...form, accepted_terms: true });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="text-center animate-fade-in">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-4">
              <Buildings size={28} weight="fill" className="text-emerald-500" />
            </div>
            <h2 data-testid="agency-register-success" className="text-xl font-medium text-white mb-2">
              Agency account created
            </h2>
            <p className="text-zinc-400 mb-6">
              Your request is under review by S.DRIVE.<br />
              You will be notified by email once approved.
            </p>
            <Button
              data-testid="agency-register-back-to-login"
              onClick={() => navigate("/login")}
              className="bg-amber-500 text-black font-semibold hover:bg-amber-600"
            >
              Back to Login
            </Button>
          </div>
        </div>
        <PublicFooter />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden flex flex-col">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/52e0b8dc-c060-4dc7-8712-962da5ad03fa/images/0d3764806606caca2d24fe0a5eb45c5247a9ddbb581b22b9b425989daccd5891.png)`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
      <div className="relative z-10 flex flex-col flex-1">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-sm animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
            <Buildings size={28} weight="fill" className="text-amber-500" />
          </div>
          <h1 className="text-3xl font-light tracking-tight text-white">S.DRIVE</h1>
          <p className="text-sm text-zinc-500 tracking-widest uppercase mt-1">Agency / Private Client</p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="bg-[#121212] border border-zinc-800 rounded-lg p-6 space-y-5"
        >
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Agency / Company Name (or your full name)</Label>
            <Input
              data-testid="agency-reg-name"
              value={form.name}
              onChange={onChange("name")}
              required
              className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-11"
              placeholder="Agency or private client name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Contact Person</Label>
            <Input
              data-testid="agency-reg-contact"
              value={form.contact_name}
              onChange={onChange("contact_name")}
              className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-11"
              placeholder="Full name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
            <Input
              data-testid="agency-reg-email"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              required
              className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-11"
              placeholder="contact@agency.com"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Phone</Label>
            <Input
              data-testid="agency-reg-phone"
              value={form.phone}
              onChange={onChange("phone")}
              className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-11"
              placeholder="+33 ..."
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password</Label>
            <Input
              data-testid="agency-reg-password"
              type="password"
              value={form.password}
              onChange={onChange("password")}
              required
              className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-11"
              placeholder="Min 4 characters"
            />
          </div>
          <Button
            data-testid="agency-reg-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 active:scale-[0.98] h-11"
          >
            {loading ? "Creating..." : "Create Agency Account"}
          </Button>

          <LegalAcceptCheckbox
            checked={form.accepted_terms}
            onChange={(v) => setForm((p) => ({ ...p, accepted_terms: v }))}
            testid="agency-reg-legal"
          />

          <p className="text-center text-sm text-zinc-500">
            Already registered?{" "}
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-amber-500 hover:text-amber-400"
            >
              Sign in
            </button>
          </p>
        </form>
          <BookWithoutAccountCTA testid="agency-register-book-no-account" />
          </div>
        </div>
        <PublicFooter />
      </div>
    </div>
  );
}
